import {StudioDashboard} from './StudioDashboard';
import {dashboardLook} from '../shared/dashboard-look';
import {SeasonElevation} from './SeasonElevation';
import {StudentHeaderActions} from './StudentHeaderActions';
import {BRAND} from '../shared/brand.js';
import {RewardRules} from './engagement/RewardRules';
import {
  ArrowRight,
  CheckCircle,
  ClockCountdown,
  Flag,
  Gauge,
  Gift,
  LockSimple,
  PushPin,
  Sparkle,
  Star,
  TrendUp,
} from "@phosphor-icons/react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { VAULT_EXAMPLES, clampPercent, type Band, unitLabel } from "../shared/economy";
import { api } from "./api";
import "./math/math.css";
import {EngagementPanel} from "./engagement/EngagementPanel";
import { KidGuide } from "./KidGuide";
import { SummitRun } from "./SummitRun";
import { SeasonMountain } from "./SeasonMountain";
import { SummitMark,headerAppName } from "./Brand";
import { burstConfetti, celebrationMemory, countUp, setSoundEnabled, soundEnabled, sounds } from "./effects";
import { LaunchOverlay } from "./LaunchOverlay";
import { motivationLine, progressLine, type MotivationEvent } from "./motivation";
import { fallbackDescription, PrizeArt } from "./prizeArt";
import type { PinnedPrize, Prize, StudentDashboardData } from "./types";

import {countdownText,friendlyDate} from "../shared/math/test-plan";
const MathLearning=lazy(()=>import("./math/MathLearning").then(m=>({default:m.MathLearning})));

interface StudentDashboardProps {
  onLogout: () => Promise<void>;
  /** Parent looking over a kid's shoulder: read-only, no logging or redeeming. */
  previewStudentId?: string;
  onExitPreview?: () => void;
  onReviewMath?: () => void;
}

export function StudentDashboard({ onLogout, previewStudentId, onExitPreview, onReviewMath }: StudentDashboardProps) {
  const preview = Boolean(previewStudentId);
  const endpoint = preview ? `/api/parent/students/${previewStudentId}/dashboard` : "/api/student/dashboard";
  const [data, setData] = useState<StudentDashboardData | null>(null);
  const [error, setError] = useState("");
  const [logging, setLogging] = useState(false);
  const [summiting, setSummiting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<number | null>(null);
  const [wishOpen, setWishOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(soundEnabled());
  // Must live with the other hooks. There is an early return for the loading
  // state below, so a useState declared after it runs on some renders and not
  // others, which is React error #310 and takes the whole dashboard down.
  const [showAll, setShowAll] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [mathOpen, setMathOpen] = useState(()=>!preview&&window.location.pathname.replace(/\/$/,'')==='/math');
  const [game, setGame] = useState<{ enabled: boolean; unlocked: boolean; climbsLeft: number; climbsPerDay?: number; clearedCount: number; bonusRun?: { earned: boolean; reason: "lesson" | "quiz" | null } } | null>(null);
  const fuelRef = useRef<HTMLElement | null>(null);
  const previousBalance = useRef<number | null>(null);

  const load = useCallback(async () => {
    const next = await api<StudentDashboardData>(endpoint);
    setData(next);
    return next;
  }, [endpoint]);

  useEffect(() => {
    void load().then((next) => {
      if (!preview) celebrate(next);
    }).catch(e=>setError(e instanceof Error?e.message:"Could not load the dashboard"));

  }, [load, preview]);

  useEffect(() => {
    if (preview) return;
    void api<{ enabled: boolean; unlocked: boolean; climbsLeft: number; climbsPerDay: number; clearedCount: number; bonusRun?: { earned: boolean; reason: "lesson" | "quiz" | null } }>("/api/student/game")
      .then(setGame).catch(() => setGame(null));
  }, [preview, data?.todayEntry]);

  useEffect(() => {
    if (!data) return;
    const previous = previousBalance.current;
    previousBalance.current = data.balances.spendable;
    if (previous !== null && data.balances.spendable > previous) {
      return countUp(fuelRef.current, previous, data.balances.spendable);
    }
    if (fuelRef.current) fuelRef.current.textContent = data.balances.spendable.toLocaleString();
    return undefined;
  }, [data]);

  if(mathOpen&&!preview)return <Suspense fallback={<main className="loader-screen">Opening {BRAND.math}…</main>}><MathLearning onBack={()=>{setMathOpen(false);if(window.location.pathname.replace(/\/$/,'')==='/math')window.history.replaceState(null,'','/');void load().catch(e=>setError(e instanceof Error?e.message:'Could not refresh the dashboard'));}}/></Suspense>;

  if (!data) {
    return (
      <main className="loader-screen student-loader">
        <div className="loader-orbit"><SummitMark size={38} /></div>
        <span>{error||"Opening your dashboard…"}</span>{error&&<><button className="button primary" onClick={()=>void load().catch(e=>setError(e.message))}>Try again</button><button className="button ghost" onClick={()=>void onLogout()}>Back to sign-in</button></>}
      </main>
    );
  }

  const look=data.dashboardLook??dashboardLook(data.student);
  const isStudio=look==='studio';
  const band = isStudio?'D':data.student.band;
  const amount = data.todayEntry?.amount ?? 0;
  const target = data.student.daily_target;
  const summited = amount >= target;
  const percent = clampPercent(amount, target);
  const remaining = Math.max(0, target - amount);
  const currency = data.settings.currencyName;
  const verified = data.todayEntry?.status === "verified";
  const logLocked = data.mathProgress ? data.mathProgress.external_status === "verified" || data.mathProgress.external_source !== "manual" : verified;
  const isXp = data.student.unit_type === "xp";

  function say(event: MotivationEvent, extra: Partial<Parameters<typeof motivationLine>[2]> = {}, source?: StudentDashboardData) {
    const d = source ?? data;
    if (!d) return;
    const rest = Math.max(0, d.student.daily_target - (d.todayEntry?.amount ?? 0));
    const line = motivationLine(d.dashboardLook==='studio'?'D':d.student.band, event, {
      name: d.student.name,
      remaining: rest,
      unit: unitLabel(d.student.unit_type, rest),
      streak: d.streak.current,
      currency: d.settings.currencyName,
      ...extra,
    }, d.dashboardLook);
    if (line) setNote(line);
  }

  // Celebration pass: unlocks (any visible prize) and quarter-milestones
  // toward the pinned expedition.
  function celebrate(next: StudentDashboardData, options: { silent?: boolean } = {}) {
    const memory = celebrationMemory(next.student.id);
    const visible = [...next.prizes, ...(next.pinned ? [next.pinned] : [])];
    const affordableIds = visible.filter((prize) => prize.affordable).map((prize) => prize.id);
    const newlyUnlocked = visible.filter((prize) => prize.affordable && !memory.unlockedSeen.includes(prize.id));
    const quarterSeen = { ...memory.quarterSeen };
    let milestonePrize: PinnedPrize | null = null;
    if (next.pinned && !next.pinned.affordable) {
      const quarter = Math.floor((next.pinned.progress ?? 0) / 25);
      if ((quarterSeen[next.pinned.id] ?? 0) < quarter) milestonePrize = next.pinned;
      quarterSeen[next.pinned.id] = Math.max(quarterSeen[next.pinned.id] ?? 0, quarter);
    }
    memory.save({ spendable: next.balances.spendable, unlockedSeen: [...new Set([...memory.unlockedSeen, ...affordableIds])], quarterSeen });
    if (options.silent || memory.spendable === null) return;
    const nextBand = next.dashboardLook==='studio'?'D':next.student.band;
    const prize = newlyUnlocked.sort((a, b) => b.cost - a.cost)[0];
    if (prize) {
      sounds.unlock(nextBand);
      burstConfetti(undefined, nextBand === "E" ? 14 : 30);
      say("unlocked", { prizeName: prize.name }, next);
    } else if (milestonePrize) {
      sounds.milestone(nextBand);
      if (nextBand !== "E") burstConfetti(undefined, 14);
      say("milestone", {
        prizeName: milestonePrize.name,
        away: milestonePrize.away,
        percentToPrize: milestonePrize.progress,
      }, next);
    }
  }

  async function logEntry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview) return;
    const nextAmount = Number(new FormData(event.currentTarget).get("amount"));
    const wasSummited = summited;
    const hadCheckpoint = data!.streak.current > 0 && data!.streak.current % 7 === 0;
    const balanceBefore = data!.balances.balance;
    setLogging(true);
    setError("");
    setNote(null);
    sounds.tap(band);
    try {
      await api("/api/student/entries", { method: "POST", body: JSON.stringify({ amount: nextAmount }) });
      const next = await load();
      const loggedTotal=next.todayEntry?.amount??nextAmount;
      const nowSummited = loggedTotal >= target;
      const gained = next.balances.balance - balanceBefore;
      if (!wasSummited && nowSummited) {
        setSummiting(true);
        if (loggedTotal > target) say("surplus", {}, next);
        else say("liftoff", {}, next);
      } else if (nowSummited && loggedTotal > target) {
        sounds.fuelGain(band, 4);
        say("surplus", {}, next);
      } else if (!nowSummited) {
        if (gained > 0) sounds.fuelGain(band, Math.min(6, gained));
        const rest = Math.max(0, target - loggedTotal);
        say(clampPercent(loggedTotal, target) >= 80 ? "save_almost" : "save_below", {
          remaining: rest,
          unit: unitLabel(next.student.unit_type, rest),
        }, next);
      }
      if (!hadCheckpoint && next.streak.current > 0 && next.streak.current % 7 === 0) {
        say("streak_checkpoint", {}, next);
        if (band !== "E") burstConfetti(undefined, 20);
      }
      celebrate(next, { silent: !wasSummited && nowSummited });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save today's work");
    } finally {
      setLogging(false);
    }
  }

  async function redeem(prize: Prize) {
    if (preview) return;
    if (!window.confirm(`Send "${prize.name}" (${prize.cost} ${currency}) to your parent?`)) return;
    try {
      await api("/api/student/redemptions", { method: "POST", body: JSON.stringify({ prizeId: prize.id }) });
      sounds.milestone(band);
      say("redeem_sent", { prizeName: prize.name });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not request that prize");
    }
  }

  // Ask for something to come off the shelf. A parent decides; nothing about
  // the balance changes either way.
  async function notForMe(prize: { id: string; name: string }) {
    if (!confirm(`Ask a grown-up to take "${prize.name}" off your shelf?`)) return;
    try {
      await api("/api/student/wishlist", { method: "POST", body: JSON.stringify({ kind: "remove", prizeId: prize.id }) });
      await load();
    } catch { /* surfaced by the next refresh */ }
  }

  async function pin(prize: Prize) {
    if (preview) return;
    try {
      await api("/api/student/pin", { method: "POST", body: JSON.stringify({ prizeId: prize.id }) });
      sounds.milestone(band);
      setNote(isStudio || band === "E" ? `Goal set: ${prize.name}.` : `New goal locked in: ${prize.name}. Every climb counts toward it now. 🎯`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not set that goal");
    }
  }

  const storeName = isStudio ? "Rewards" : band === "A" ? "Star Chart" : "Trading Post";
  const SHELF_LIMIT = 6;
  // A shelf of thirty things is a catalog, not a choice. Show a short set by
  // default, keep the pinned goal always visible, and let the browse drawer and
  // the wish button handle everything else. Kids choose worse from long lists,
  // and a parent who ran the starter set twice should not be punished for it.
  const filtered = tierFilter === null ? data.prizes : data.prizes.filter((prize) => prize.tier === tierFilter);
  // Nearest-first, so what they can nearly afford is what they see.
  const ranked = [...filtered].sort((a, b) => (a.away ?? 0) - (b.away ?? 0));
  const pinnedId = data.pinned?.id;
  const shortlist = ranked.filter((prize) => prize.id !== pinnedId).slice(0, SHELF_LIMIT);
  const visiblePrizes = showAll || tierFilter !== null
    ? filtered
    : ranked.filter((prize) => prize.id === pinnedId || shortlist.includes(prize));
  const hiddenCount = filtered.length - visiblePrizes.length;
  const tiersPresent = [...new Set(data.prizes.map((prize) => prize.tier))].sort();

  const rewards=<>
        <section className="shop-section" id="trading-post">
          <VaultCard preview={preview} vault={data.vault} spendable={data.balances.spendable} currency={currency} band={band} onChanged={() => void load()} />

          {/* Three states, and they used to be two.
              "You have not earned it yet" and "you have had your three runs"
              rendered identically: same grey card, same dead button. A parent
              reported the game as broken because a child who had earned it,
              played all three runs and come back later saw a locked card that
              said nothing about having already played. Those are opposite
              messages and they now look opposite.
              Being out of runs also no longer disables the card. The mountain
              entries and the score boards live behind it, and neither of those
              is a reward that needs rationing. */}
          {!isStudio && game?.enabled && (
            <button
              className={`run-card ${!game.unlocked ? "locked" : game.climbsLeft > 0 ? "ready" : "done"}`}
              onClick={() => { if (game.unlocked) setRunOpen(true); }}
              disabled={!game.unlocked}
            >
              <span className="run-card-goat" aria-hidden="true">{game.unlocked && game.climbsLeft === 0 ? "🎉" : "🐐"}</span>
              <span className="run-card-copy">
                <strong>{BRAND.gameName}</strong>
                <small>
                  {!game.unlocked
                    ? `Finish today's ${unitLabel(data.student.unit_type, target)} and this opens`
                    : game.climbsLeft === 0
                      ? `You skied all ${game.climbsPerDay ?? 3} today. Come read about the mountains.`
                      : `${game.climbsLeft} run${game.climbsLeft === 1 ? "" : "s"} left today · ${game.clearedCount} of 10 mountains skied`}
                </small>
                {/* The bonus run (shared/bonus-run.ts): named when earned, and offered as a goal until then. */}
                {game.bonusRun?.earned
                  ? <small className="run-card-bonus">⭐ Bonus run for a perfect {game.bonusRun.reason ?? "lesson"}!</small>
                  : game.bonusRun && <small className="run-card-bonus-hint">A perfect lesson or quiz earns a bonus run</small>}
              </span>
              {game.unlocked && (
                <span className="run-card-go">{game.climbsLeft > 0 ? "Play" : "Open"}</span>
              )}
            </button>
          )}

          <KidGuide look={look} band={band} currency={currency} />

          {!isStudio&&<SeasonCard season={data.season} unitType={data.student.unit_type} band={band} />}

          {!preview&&<BrowseCatalog currency={currency} onAsked={() => void load()} />}

          {data.myPrizes.length > 0 && (
            <div className="trophy-shelf">
              <div className="section-heading"><div>
                <span className="section-kicker"><Star size={18} weight="fill" /> Your shelf</span>
                <h2>Prizes you have earned</h2>
              </div></div>
              <ul>
                {data.myPrizes.map((prize) => (
                  <li key={prize.id} className={prize.fulfilled_at ? "done" : "owed"}>
                    <div>
                      <strong>{prize.prize_name_snapshot}</strong>
                      <small>{prize.cost_snapshot} {currency}</small>
                    </div>
                    <span className={`shelf-chip ${prize.fulfilled_at ? "done" : "owed"}`}>
                      {prize.fulfilled_at ? <><CheckCircle weight="fill" /> Got it</> : <><ClockCountdown weight="fill" /> Coming</>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="section-heading">
            <div>
              <span className="section-kicker"><Gift size={18} weight="fill" /> {storeName}</span>
              <h2>{data.storeUnlocked ? "Open for business" : data.canBrowse ? "Pick your next big goal" : "Locked until you summit"}</h2>
            </div>
          </div>

          {!data.storeUnlocked && !data.canBrowse ? (
            <div className="store-locked">
              <LockSimple size={30} weight="fill" />
              <div>
                <strong>The {storeName} opens after you summit today.</strong>
                <span>
                  {data.storeSize > 0
                    ? `${data.storeSize} prize${data.storeSize === 1 ? "" : "s"} waiting on the other side. ${remaining > 0 ? `${remaining} ${unitLabel(data.student.unit_type, remaining)} to go.` : ""}`
                    : "Ask a parent to stock it from the prize library."}
                </span>
              </div>
            </div>
          ) : (
            <>
              {tiersPresent.length > 1 && (
                <div className="tier-chips">
                  <button className={`tier-chip ${tierFilter === null ? "active" : ""}`} onClick={() => setTierFilter(null)}>All prizes</button>
                  {tiersPresent.map((tier) => (
                    <button key={tier} className={`tier-chip ${tierFilter === tier ? "active" : ""}`} onClick={() => setTierFilter(tier)}>
                      Tier {tier}
                    </button>
                  ))}
                </div>
              )}
              {data.pendingRedemption && (
                <div className="pending-banner">
                  <ClockCountdown size={22} weight="fill" />
                  <div>
                    <strong>Request on Parent dashboard</strong>
                    <span>A parent is reviewing {String(data.pendingRedemption.prize_name_snapshot)}.</span>
                  </div>
                </div>
              )}
              <div className="prize-grid">
                {visiblePrizes.map((prize) => (
                  <PrizeCard
                    key={prize.id}
                    look={look}
                    prize={prize}
                    currency={currency}
                    band={band}
                    disabled={preview || Boolean(data.pendingRedemption) || !data.storeUnlocked}
                    preview={preview}
                    isPinned={data.pinned?.id === prize.id}
                    canPin={!preview}
                    onRedeem={() => void redeem(prize)}
                    onPin={() => void pin(prize)}
                    onNotForMe={preview ? undefined : () => void notForMe(prize)}
                  />
                ))}
                {visiblePrizes.length === 0 && <p className="empty-line"><Gift size={20} /> Nothing in this tier yet. Ask a parent to stock the {storeName.toLowerCase()}.</p>}
              </div>
              {hiddenCount > 0 && !showAll && (
                <button className="shelf-more" onClick={() => setShowAll(true)}>
                  Show {hiddenCount} more in your store
                </button>
              )}
              {showAll && (
                <button className="shelf-more" onClick={() => setShowAll(false)}>
                  Show fewer
                </button>
              )}
              {!data.storeUnlocked && data.canBrowse && (
                <p className="browse-note">
                  <LockSimple size={16} weight="fill" />
                  Pick the one you are working toward. You can spend once you summit today.
                </p>
              )}
            </>
          )}
        </section>

        {data.wishlist.length > 0 && (
          <section className="wish-history">
            <span className="section-kicker"><Star size={16} weight="fill" /> {isStudio?'Your requests':'Your wishes'}</span>
            <div>
              {data.wishlist.map((wish) => (
                <span key={wish.id} className={`wish-chip wish-${wish.status}`}>
                  {wish.raw_name} · {wish.status === "pending" ? "waiting" : wish.status}
                </span>
              ))}
            </div>
          </section>
        )}
  </>;

  return (
    <main className="student-shell" data-look={look} data-theme={isStudio?(data.studioAppearance?.theme??'light'):undefined}>
      {summiting && (
        <LaunchOverlay
          band={band}
          onDone={() => {
            setSummiting(false);
            burstConfetti(undefined, band === "E" ? 12 : 26);
            if (data) celebrate(data);
          }}
        />
      )}

      {isStudio?<StudioDashboard data={data} preview={preview} soundOn={soundOn}
        onSoundToggle={()=>{const next=!soundOn;setSoundOn(next);setSoundEnabled(next);if(next)sounds.tap(band);}}
        onLogout={()=>void onLogout()} onExitPreview={onExitPreview}
        onStudy={()=>{if(preview)onReviewMath?.();else setMathOpen(true);}}
        onGame={()=>setRunOpen(true)} game={game} rewards={rewards} onRequest={()=>setWishOpen(true)}
        onAppearance={appearance=>setData(current=>current?{...current,studioAppearance:appearance}:current)} onRefresh={load} note={note} error={error}
        externalLog={!preview&&!isXp?<form onSubmit={logEntry} className="quick-log-form"><label><span>{unitLabel(data.student.unit_type,2)}</span><input name="amount" type="number" min={0} max={100000} defaultValue={data.mathProgress?.external_amount??amount} disabled={logLocked} required/></label><button className="button ghost" disabled={logging||logLocked}>{logLocked?'Verified':logging?'Saving…':'Save log'}</button></form>:undefined}
      />:<>
      <header className="tb-header">
        <div className="tb-brand"><span className="tb-brand-mark"><SummitMark size={30} /></span><strong>{headerAppName(data.settings.appName)}</strong></div>
        <div className="tb-greeting">
          {preview ? <strong>👀 {data.student.name}'s view</strong> : <><span>{summited ? "Clear skies," : "Trail's open,"}</span><strong>{data.student.name}</strong></>}
        </div>
        <StudentHeaderActions preview={preview} soundOn={soundOn} onSoundToggle={()=>{const next=!soundOn;setSoundOn(next);setSoundEnabled(next);if(next)sounds.tap(band);}} onLogout={()=>void onLogout()} onExitPreview={onExitPreview}/>
      </header>

      <TimeBackBanner data={data} summited={summited} />

      <div className="tb-content">
        <EngagementPanel studentId={data.student.id} preview={preview} muted={!soundOn} refresh={data.mathXp?.total??0} onStudy={()=>setMathOpen(true)} currency={currency} onRewardsChanged={()=>void load()}/>
        <section className="math-study-card"><div><span className="section-kicker">Learn here</span><h2>{BRAND.math}</h2><p className="math-xp-summary"><strong>{data.mathXp?.today??0} Math XP today</strong> · {data.mathXp?.total??0} earned overall</p><p><strong>{BRAND.coach}:</strong> Mistakes are part of training. Let’s work on your next skill.</p>{data.testPrep&&data.testPrep.daysLeft>=0&&<p className="math-test-countdown"><strong>📝 {countdownText(data.testPrep.daysLeft)}</strong> · {friendlyDate(data.testPrep.date,data.today)}</p>}</div><div>{preview?<><button className="button primary" disabled={!onReviewMath} onClick={onReviewMath}>Review math learning →</button><p className="math-footnote">Parent preview. To practice as {data.student.name}, sign in with their student PIN.</p><button className="text-link" onClick={()=>void onLogout()}>Student sign-in</button></>:<><button className="button primary" onClick={()=>setMathOpen(true)}>Start studying →</button><button className="button ghost" onClick={()=>setMathOpen(true)}>{data.testPrep&&data.testPrep.daysLeft>=0?'Practice for the test':'📝 I have a test coming up'}</button></>}</div></section>

        <section className={`hero-card ${summited ? "is-lifted" : ""}`}>
          <div className="hero-copy">
            <span className="section-kicker">Today's climb · {formatDate(data.today)}</span>
            <h1>{summited ? <>GOAL<span> MET</span></> : <>Ready to<br /><span>climb?</span></>}</h1>
            <p className="hero-line">{note ?? progressLine(band, percent, remaining, unitLabel(data.student.unit_type, remaining))}</p>
            <div className="meta-chips">
              <span className="meta-chip">{amount.toLocaleString()} / {target.toLocaleString()} {unitLabel(data.student.unit_type, target)} today</span>
              <span className="meta-chip">🔥 {data.streak.current} day streak</span>
              <span className="meta-chip">best {data.streak.best}</span><span className="meta-chip">🧊 {data.streak.freezes??1} streak {(data.streak.freezes??1)===1?"freeze":"freezes"}</span>
            </div>
            {!preview && !isXp && (
              <form onSubmit={logEntry} className="quick-log-form">
                <label>
                  <span>{isXp?"External math XP":unitLabel(data.student.unit_type, 2)}</span>
                  <input
                    name="amount" type="number" inputMode="numeric" min={0} max={100000}
                    defaultValue={data.mathProgress?.external_amount ?? (amount || "")} placeholder={String(target)} disabled={logLocked} required
                  />
                </label>
                <button className="button primary" disabled={logging || logLocked}>
                  {logLocked ? <><CheckCircle weight="fill" /> Verified</> : logging ? "Saving…" : <>{data.todayEntry ? "Update log" : "Log it"} <ArrowRight weight="bold" /></>}
                </button>
              </form>
            )}
            {isXp && !preview && (
              <p className="reconcile-note">{BRAND.math} XP is added automatically as you work.</p>
            )}
            {verified && <p className="verified-note"><CheckCircle weight="fill" /> Checked and locked in.</p>}
            {error && <p className="form-error">{error}</p>}
          </div>
          <ProgressRing amount={amount} target={target} percent={percent} summited={summited} />
        </section>

        <div className="dashboard-grid">
          <SeasonElevation season={data.season} current={data.streak.current} best={data.streak.best} freezes={data.streak.freezes??1} message={data.streak.message}/>
          <FuelCard data={data} currency={currency} fuelRef={fuelRef} />
          <ExpeditionCard pinned={data.pinned} currency={currency} band={band} preview={preview} pending={Boolean(data.pendingRedemption)} storeUnlocked={data.storeUnlocked} onRedeem={(p) => void redeem(p)} />
        </div>

        <HistoryHeatmap data={data} />

        {rewards}
      </div>

      </>}

      {!preview && !isStudio && (
        <button className="wish-fab" onClick={() => setWishOpen(true)} title="Add to wishlist">
          <Star size={20} weight="fill" /> Wish
        </button>
      )}
      {runOpen && (
        <SummitRun
          band={band}
          onClose={() => setRunOpen(false)}
          onPlayed={() => { void api<typeof game>("/api/student/game").then(setGame).catch(() => {}); }}
        />
      )}
      {wishOpen && (
        <WishModal
          band={band}
          onClose={() => setWishOpen(false)}
          onSubmitted={async () => {
            setWishOpen(false);
            sounds.tap(band);
            say("wish_sent");
            await load();
          }}
        />
      )}

      {!isStudio&&<footer className="student-footer"><span>{BRAND.tagline}</span><small>{headerAppName(data.settings.appName)} · Family learning</small></footer>}
    </main>
  );
}

function WishModal({ band, onClose, onSubmitted }: { band: Band; onClose: () => void; onSubmitted: () => Promise<void> }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/student/wishlist", {
        method: "POST",
        body: JSON.stringify({
          name: form.get("name"),
          note: form.get("note") || undefined,
          url: form.get("url") || undefined,
        }),
      });
      await onSubmitted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not send your wish");
      setBusy(false);
    }
  }
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="modal wish-modal" role="dialog" aria-modal="true" aria-label="Add to wishlist">
        <header><div><span className="eyebrow">Wishlist</span><h2>{band === "A" ? "Make a wish ⭐" : "What do you want to earn?"}</h2></div></header>
        <form className="modal-form" onSubmit={submit}>
          <label>What is it? <input name="name" maxLength={120} required autoFocus placeholder={band === "E" ? "Concert ticket, gear, cash goal…" : "A LEGO set, a trip, a game…"} /></label>
          <label>Why do you want it? <small>optional</small><input name="note" maxLength={300} /></label>
          <label>Link <small>optional</small><input name="url" type="url" maxLength={500} placeholder="https://…" /></label>
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="button ghost" onClick={onClose}>Cancel</button>
            <button className="button primary" disabled={busy}>{busy ? "Sending…" : "Send to parent"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function TimeBackBanner({ data, summited }: { data: StudentDashboardData; summited: boolean }) {
  const { freeDays, since } = data.timeBack;
  if (freeDays === 0 && !summited) return null;
  const sinceText = since ? ` since ${new Date(`${since}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" })}` : "";
  return (
    <div className={`timeback-banner ${summited ? "lifted" : ""}`}>
      <strong>
        {freeDays > 0
          ? `You've earned ${freeDays} free ${freeDays === 1 ? "afternoon" : "afternoons"}${sinceText}.`
          : "Your first free afternoon is one good climb away."}
      </strong>
      {summited && <span className="timeback-today"><Sparkle weight="fill" /> Today: GOAL MET · free time unlocked</span>}
    </div>
  );
}

function ProgressRing({ amount, target, percent, summited }: { amount: number; target: number; percent: number; summited: boolean }) {
  const radius = 118;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className={`progress-flight ${summited ? "complete" : ""}`}>
      <svg viewBox="0 0 280 280" role="img" aria-label={`${percent}% of today's target`}>
        <defs>
          <linearGradient id="progress-gradient" x1="0" x2="1">
            <stop stopColor="#8ee0b4" /><stop offset="1" stopColor="#1e9e57" />
          </linearGradient>
          <linearGradient id="progress-gradient-done" x1="0" x2="1">
            <stop stopColor="#ffce50" /><stop offset="1" stopColor="#ff8a3d" />
          </linearGradient>
        </defs>
        <circle className="ring-track" cx="140" cy="140" r={radius} />
        <circle
          className="ring-value" cx="140" cy="140" r={radius}
          strokeDasharray={circumference} strokeDashoffset={circumference * (1 - percent / 100)}
        />
      </svg>
      <div className="ring-center">
        {summited
          ? <Flag className="ring-flag" size={64} weight="fill" />
          : <><strong>{percent}<sup>%</sup></strong><span>to the summit</span></>}
      </div>
      <div className="ring-footer"><strong>{amount.toLocaleString()}</strong><span>/ {target.toLocaleString()}</span></div>
    </div>
  );
}

function FuelCard({ data, currency, fuelRef }: { data: StudentDashboardData; currency: string; fuelRef: React.MutableRefObject<HTMLElement | null> }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="stat-card fuel-card">
      <div className="card-top"><span className="section-kicker"><Gauge size={18} weight="fill" /> {currency} supply</span><Sparkle size={20} weight="fill" className="fuel-sparkle" /></div>
      <div className="fuel-number">
        <strong ref={(node) => { fuelRef.current = node; }}>{data.balances.spendable.toLocaleString()}</strong>
        <span>{currency} ready to spend</span>
      </div>
      {data.balances.pending > 0
        ? <p className="fuel-note"><ClockCountdown weight="fill" /> +{data.balances.pending} more lands after the weekly check</p>
        : <p className="fuel-note cleared"><CheckCircle weight="fill" /> All {currency.toLowerCase()} cleared for prizes</p>}
      <button className="fuel-explain" onClick={() => setOpen(true)}>
        How do you earn {currency.toLowerCase()}?
      </button>
      {open && <StampSheet data={data} currency={currency} onClose={() => setOpen(false)} />}
    </article>
  );
}

// The pay stub.
//
// A kid who cannot say how the number got there does not believe the number,
// and a currency nobody believes does not motivate anyone. So this shows the
// whole chain in the order it actually happens: what you did today, what that
// converted to, what is still waiting on the weekly check, and what is sitting
// there ready to spend. Every figure on it is one they can check against the
// screen of the app they just came out of.
//
// It is deliberately not called a paycheck. Nobody is employing them, and the
// point of the design is that the work is the thing and the stamps are the
// receipt.
function StampSheet({data,currency,onClose}:{data:StudentDashboardData;currency:string;onClose:()=>void}) {
 const dialog=useRef<HTMLDialogElement>(null);
 useEffect(()=>{const node=dialog.current;node?.showModal();return()=>node?.close();},[]);
 return <dialog ref={dialog} className="study-bucks-dialog" aria-label={`How you earn ${currency}`} onCancel={e=>{e.preventDefault();onClose();}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}><div className="stamp-sheet"><header><h3>How you earn {currency.toLowerCase()}</h3><button className="small-button" onClick={onClose}>Close</button></header><StudyBucksSummary data={data} currency={currency}/></div></dialog>;
}
export function StudyBucksSummary({data,currency}:{data:StudentDashboardData;currency:string}) {
 return <><RewardRules currency={currency}/>
 <p>A parent can add a bonus with a note. Prize requests go to your parent; {currency.toLowerCase()} leave your balance when the prize is delivered.</p><h4>Recent earnings and spending</h4><ol className="stamp-steps">{(data.earningsHistory??[]).map(row=><li key={row.id}><div><strong>{row.note??'Account history'}</strong><small>{row.created_at.slice(0,10)}</small></div><b>{row.delta>0?'+':''}{row.delta}</b></li>)}</ol>{!(data.earningsHistory?.length)&&<p>Your earnings will appear here as you practice.</p>}<p className="stamp-foot">Your earned bucks stay yours after a day off or a tricky problem.</p></>;
}

function ExpeditionCard({ pinned, currency, band, preview, pending=false, storeUnlocked, onRedeem }: {
  pinned: PinnedPrize | null; currency: string; band: Band; preview: boolean; pending?:boolean; storeUnlocked: boolean;
  onRedeem: (prize: Prize) => void;
}) {
  if (!pinned) {
    return (
      <article className="stat-card closest-card">
        <div className="card-top"><span className="section-kicker"><PushPin size={18} weight="fill" /> Your goal</span></div>
        <PushPin size={30} className="muted" />
        <h3>No goal picked yet</h3>
        <p className="muted" style={{ fontSize: 12 }}>
          {storeUnlocked
            ? `Open the ${band === "A" ? "Star Chart" : "Trading Post"} below and pin the prize you're climbing toward.`
            : "Reach your goal today, then pick the prize you're climbing toward."}
        </p>
      </article>
    );
  }
  const affordable = Boolean(pinned.affordable);
  return (
    <article className={`stat-card closest-card ${affordable ? "affordable" : ""}`}>
      <div className="card-top">
        <span className="section-kicker"><PushPin size={18} weight="fill" /> {affordable ? "Goal reached!" : "Your goal"}</span>
        <span className={`tier-badge tier-${pinned.tier}`}>Tier {pinned.tier}</span>
      </div>
      <div className={`prize-visual tier-tint-${pinned.tier}`}>
        {pinned.image_url ? <img src={pinned.image_url} alt="" /> : <PrizeArt name={pinned.name} />}
      </div>
      <h3>{pinned.name}</h3>
      <div className="prize-progress"><i style={{ width: `${pinned.progress ?? 0}%` }} /></div>
      <div className="prize-meta">
        <span>{affordable ? "Ready to request ✓" : `${(pinned.away ?? 0).toLocaleString()} ${currency} away`}</span>
        <strong>{pinned.cost.toLocaleString()}</strong>
      </div>
      {affordable && !preview
        ? <button className="button primary" disabled={pending} onClick={() => onRedeem(pinned)}>{pending?"Request pending":"Request prize"}</button>
        : <span className="repin-note">{pinned.repinDaysLeft > 0 ? `You can change your goal in ${pinned.repinDaysLeft} day${pinned.repinDaysLeft === 1 ? "" : "s"}` : "You can change your goal now"}</span>}
    </article>
  );
}

function HistoryHeatmap({ data }: { data: StudentDashboardData }) {
  const target = data.student.daily_target;
  return (
    <section className="history-card">
      <div className="section-heading">
        <div>
          <span className="section-kicker"><Gauge size={18} weight="fill" /> Climb log</span>
          <h2>Last 14 days</h2>
        </div>
        <span className="target-key">■ summited · every green square is a free afternoon</span>
      </div>
      <div className="heatmap">
        {data.history.map((day) => {
          const ratio = Math.min(1, day.amount / Math.max(1, target));
          const met = day.amount >= target;
          const level = day.amount === 0 ? 0 : met ? 4 : ratio >= 0.66 ? 3 : ratio >= 0.33 ? 2 : 1;
          return (
            <div className="heatmap-day" key={day.date} title={`${day.date}: ${day.amount}`}>
              <div className={`heatmap-cell level-${level}`}>{met && <Flag size={13} weight="fill" />}</div>
              <span>{new Date(`${day.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "narrow" })}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function PrizeCard({ prize, currency, band, disabled, preview, isPinned, canPin, onRedeem, onPin, onNotForMe,look='adventure' }: {
  look?:'adventure'|'studio';
  prize: Prize; currency: string; band: Band; disabled: boolean; preview: boolean;
  isPinned: boolean; canPin: boolean; onRedeem: () => void; onPin: () => void;
  onNotForMe?: () => void;
}) {
  const affordable = Boolean(prize.affordable);
  return (
    <article className={`shop-card tier-card-${prize.tier} ${affordable ? "affordable" : ""} ${isPinned ? "pinned" : ""}`}>
      <div className={`shop-image tier-tint-${prize.tier}`}>
        {prize.image_url ? <img src={prize.image_url} alt="" /> : <PrizeArt name={prize.name} />}
        {isPinned && <span className="unlocked-flag pinned-flag"><PushPin weight="fill" /> Your goal</span>}
        {affordable && !isPinned && <span className="unlocked-flag"><Sparkle weight="fill" /> Unlocked</span>}
      </div>
      <div className="shop-card-body">
        <span className={`tier-badge tier-${prize.tier}`}>Tier {prize.tier}</span>
        <h3>{prize.name}</h3>
        <p>{prize.description ?? fallbackDescription(prize.name, prize.band)}</p>{prize.weekly_limit&&<small>Up to {prize.weekly_limit} each week · parent approval required</small>}
        <div className="prize-progress"><i style={{ width: `${prize.progress ?? 0}%` }} /></div>
        <div className="prize-meta">
          <span>{affordable ? "Ready to request ✓" : `${(prize.away ?? 0).toLocaleString()} ${currency} to go`}</span>
          <strong>{prize.cost.toLocaleString()} <small>{currency}</small></strong>
        </div>
        <div className="prize-actions">
          <button
            className={`button ${affordable ? "primary" : "disabled"}`}
            disabled={!affordable || disabled}
            onClick={onRedeem}
          >
            {affordable
              ? preview ? "Redeem (kid only)" : disabled ? "Request pending" : look==='studio'?"Request":"Redeem"
              : <><LockSimple weight="fill" /> {look==='studio'?"Keep practicing":band === "A" ? "Keep earning" : "Keep climbing"}</>}
          </button>
          {onNotForMe && (
            <button className="button ghost pin-button" onClick={onNotForMe} aria-label={`Ask to replace ${prize.name}`} title="Not for me, ask to swap it out">✕</button>
          )}
          {canPin && !isPinned && (
            <button className="button ghost pin-button" onClick={onPin} aria-label={`Make ${prize.name} your goal`} title="Make this your goal"><PushPin weight="bold" /></button>
          )}
        </div>
      </div>
    </article>
  );
}

function formatDate(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}


// Everything in the catalog for this kid's age that is not already on their
// shelf. Asking is not spending, so this is never gated: a kid who can see
// what exists and say "that one instead" has some say in their own shelf,
// which is the cheapest autonomy in the product. A parent approves every ask.
function BrowseCatalog({ currency, onAsked }: { currency: string; onAsked: () => void }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Array<{ id: string; name: string; description: string | null; category: string; tier: number; cost: number }>>([]);
  const [asked, setAsked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || items.length) return;
    void api<{ prizes: typeof items }>("/api/student/catalog").then((data) => setItems(data.prizes)).catch(() => setItems([]));
  }, [open]);

  async function ask(id: string) {
    setBusy(id);
    try {
      await api("/api/student/wishlist", { method: "POST", body: JSON.stringify({ kind: "catalog", prizeId: id }) });
      setAsked({ ...asked, [id]: true });
      onAsked();
    } catch { /* the button state stays put; they can tap again */ }
    finally { setBusy(""); }
  }

  return (
    <div className="browse-card">
      <button className="browse-toggle" onClick={() => setOpen(!open)}>
        <span><Gift size={18} weight="fill" /> See everything kids your age can pick</span>
        <span>{open ? "Hide" : "Look"}</span>
      </button>
      {open && (
        <>
          <p className="muted" style={{ fontSize: 13, margin: "12px 0 0" }}>
            Tap ask on anything you would rather have. A grown-up says yes or no.
          </p>
          <div className="browse-grid">
            {items.map((item) => (
              <div className="browse-item" key={item.id}>
                <div>
                  <strong>{item.name}</strong>
                  <small>Tier {item.tier} · {item.cost} {currency}</small>
                </div>
                <button
                  className="small-button"
                  disabled={Boolean(asked[item.id]) || busy === item.id}
                  onClick={() => void ask(item.id)}
                >
                  {asked[item.id] ? "Asked" : busy === item.id ? "…" : "Ask"}
                </button>
              </div>
            ))}
            {items.length === 0 && <p className="empty-line"><Gift size={20} /> Nothing else to show right now.</p>}
          </div>
        </>
      )}
    </div>
  );
}


// The year-long climb. Distinct from the streak, which can end after missed scheduled days,
// and from the balance, which drops every time a prize is bought. This only
// ever goes up, which is the entire point: a kid needs one number that no
// mistake and no purchase can take away from them.
function SeasonCard({ season, unitType, band }: {
  season: StudentDashboardData["season"]; unitType: string; band: Band;
}) {
  // Celebrate a newly summited peak once, the first time the kid sees it.
  // Kept in localStorage rather than the server: missing a party because a
  // browser was cleared is a far smaller cost than storing more about a child.
  useEffect(() => {
    const latest = season.passed[season.passed.length - 1];
    if (!latest || typeof window === "undefined") return;
    const key = `summit:peak:${latest.label}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, "1");
    } catch {
      return; // private browsing; skip the party rather than crash the page
    }
    burstConfetti();
    sounds.milestone(band);
  }, [season.passed.length, band]);

  return (
    <article className="season-card">
      <div className="season-copy">
        <span className="section-kicker light"><TrendUp weight="fill" /> This school year</span>
        <div className="season-number">
          <strong>{season.feet.toLocaleString()}</strong><span>feet climbed</span>
        </div>
        <p className="season-sub">
          {season.climbingDays} {season.climbingDays === 1 ? "day" : "days"} on the mountain
          {season.units > 0 && ` · ${season.units.toLocaleString()} ${unitLabel(unitType as never, season.units)} banked`}
        </p>
      </div>

      <SeasonMountain feet={season.feet} passed={season.passed} next={season.next} waypoints={season.waypoints} />

      {season.next ? (
        <>
          <p className="season-next">
            Next up: <strong>{season.next.label}</strong>, {season.next.where} · {(season.next.feet - season.feet).toLocaleString()} ft to go
          </p>
          <div className="season-bar"><i style={{ width: `${season.percentToNext}%` }} /></div>
        </>
      ) : (
        <p className="season-next"><strong>🚀 Off the map. There is nothing left above you.</strong></p>
      )}

      {season.lastSeason?.summit && (
        <p className="season-last">
          Last year you reached <strong>{season.lastSeason.summit.label}</strong> in {season.lastSeason.climbingDays} days.
        </p>
      )}

      {/* Badges are their own reward, nothing to do with the prize shelf. */}
      <div className="peak-badges">
        {season.waypoints.map((point) => {
          const done = season.passed.some((p) => p.label === point.label);
          return (
            <div key={point.label} className={`peak-badge ${done ? "earned" : ""}`} title={point.blurb}>
              <span className="peak-emoji">{done ? point.badge : "🔒"}</span>
              <strong>{point.label}</strong>
              <small>{done ? point.where : `${point.feet.toLocaleString()} ft`}</small>
            </div>
          );
        })}
      </div>

      <p className="season-note">
        {band === "A" ? "Stars you spend never lower this." : "Spending never lowers this. It only goes up."}
      </p>
    </article>
  );
}


// Savings, with the incentive the right way round. Alpha charges 50% to take
// money out early; this pays a bonus for leaving it in. Same lesson about
// delayed gratification, except a kid who changes their mind loses a bonus
// they never had rather than currency they earned.
function VaultCard({ vault, spendable, currency, band, onChanged,preview=false }: {
  preview?:boolean;
  vault: StudentDashboardData["vault"]; spendable: number; currency: string; band: Band; onChanged: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pct = Math.round(vault.bonusRate * 100);
  const typed = Math.max(0, parseInt(amount, 10) || 0);
  const typedBonus = Math.floor(typed * vault.bonusRate);
  // The smallest deposit whose bonus does not floor to zero.
  const minToEarn = Math.ceil(1 / vault.bonusRate);

  async function deposit() {
    const value = parseInt(amount, 10);
    if (!Number.isFinite(value) || value < 1) { setError("Pick an amount to save"); return; }
    setBusy(true); setError("");
    try {
      await api("/api/student/vault/deposit", { method: "POST", body: JSON.stringify({ amount: value }) });
      setAmount("");
      sounds.fuelGain(band, parseInt(amount, 10) || 0);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save that");
    } finally { setBusy(false); }
  }

  async function withdraw(depositId: string) {
    setBusy(true); setError("");
    try {
      const result = await api<{ bonus: number }>("/api/student/vault/withdraw", {
        method: "POST", body: JSON.stringify({ depositId }),
      });
      if (result.bonus > 0) { burstConfetti(); sounds.milestone(band); }
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not take that out");
    } finally { setBusy(false); }
  }

  return (
    <article className="vault-card">
      <div className="section-heading"><div>
        <span className="section-kicker"><Star weight="fill" /> Savings</span>
        <h2 style={{ fontSize: 20 }}>{vault.held.toLocaleString()} {currency} saved</h2>
        <p>Leave it {vault.maturityDays} days and it grows by {pct}%. Change your mind any time and you get back exactly what you put in.</p>
      </div></div>

      {vault.deposits.length > 0 && (
        <ul className="vault-list">
          {vault.deposits.map((item) => (
            <li key={item.id} className={item.matured ? "ready" : ""}>
              <div>
                <strong>{item.amount.toLocaleString()} {currency}</strong>
                <small>
                  {item.matured
                    ? `Ready · +${item.bonus} bonus waiting`
                    : `${item.daysLeft} day${item.daysLeft === 1 ? "" : "s"} to go · +${Math.floor(item.amount * vault.bonusRate)} if you wait`}
                </small>
              </div>
              <button className={`small-button ${item.matured ? "approve" : ""}`} disabled={busy||preview} onClick={() => void withdraw(item.id)}>
                {item.matured ? `Take ${item.total}` : "Take it back"}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="vault-deposit">
        <input
          type="number" min={1} max={Math.max(1, spendable)} value={amount} placeholder="0"
          onChange={(event) => setAmount(event.target.value)} aria-label={`Amount to save`}
        />
        <button className="button primary" disabled={busy || preview || spendable < 1} onClick={() => void deposit()}>
          Save it
        </button>
      </div>

      {/* The whole point of a savings feature for a child is the arithmetic, so
          show the working rather than just the answer. */}
      {typed > 0 ? (
        <div className={`vault-math ${typedBonus > 0 ? "pays" : "tiny"}`}>
          {typedBonus > 0 ? (
            <>
              <strong>{typed} + {typedBonus} = {typed + typedBonus} {currency}</strong>
              <span>{pct}% of {typed} is {typedBonus}. Wait {vault.maturityDays} days and that is yours.</span>
            </>
          ) : (
            <>
              <strong>{typed} + 0 = {typed} {currency}</strong>
              <span>{pct}% of {typed} rounds down to nothing. Save at least {minToEarn} to earn a bonus.</span>
            </>
          )}
        </div>
      ) : (
        <div className="vault-examples">
          <span>{pct}% means: </span>
          {VAULT_EXAMPLES.map((example) => (
            <button key={example} type="button" onClick={() => setAmount(String(example))} disabled={example > spendable}>
              {example} → +{Math.floor(example * vault.bonusRate)}
            </button>
          ))}
        </div>
      )}

      <small className="vault-hint">{spendable.toLocaleString()} {currency} available to save</small>
      {error && <p className="form-error">{error}</p>}
    </article>
  );
}
